import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  fetchPackages,
  createPackage,
  updatePackage,
  deletePackage,
  createPaymentPackage,
  fetchPaymentPackage,
  deletePaymentPackage,
  updatePaymentPackage,
} from "./thunks";
import { Package } from "@/types";
import { toast } from "react-toastify";

interface PaginatedPackages {
  data: Package[];
  total?: number;
  currentPage?: number;
  perPage?: number;
}

interface PaginatedPaymentPackages {
  data: PaymentPackage[];
  total?: number;
  currentPage?: number;
  perPage?: number;
}

interface PaymentPackage {
  id: string;
  client_id: string;
  package_id: string;
  discount_amount: number | null;
  //tip_amount: number | null;
  payment_details: {
    paid_amount: number | null;
    payment_method_id: string;
  }[];
}

interface PackagesState {
  packages: PaginatedPackages;
  paymentPackage: PaginatedPaymentPackages;
  loading: boolean;
  error: string | null;
}

const initialState: PackagesState = {
  packages: { data: [] },
  paymentPackage: { data: [] },
  loading: false,
  error: null,
};

const PackageSlice = createSlice({
  name: "packages",
  initialState,
  reducers: {
    clearSuppliers: (state) => {
      state.packages = { data: [] };
      state.loading = false;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch
      .addCase(fetchPackages.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPackages.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading = false;
        const payload = action.payload;

        if (Array.isArray(payload.data)) {
          // Dados não paginados
          state.packages = { data: payload.data };
        } else {
          // Dados paginados
          state.packages = {
            data: payload.data.data,
            total: payload.data.total,
            currentPage: payload.data.current_page,
            perPage: payload.data.per_page,
          };
        }
      })
      .addCase(fetchPackages.rejected, (state, action) => {
        state.error = (action.payload as { error: string }).error;
        state.loading = false;
      })
      // Create
      .addCase(createPackage.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createPackage.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.packages.data.push(action.payload.data);
        toast.success("Pacote cadastrado com sucesso!");
      })
      .addCase(createPackage.rejected, (state, action) => {
        const payload = action.payload as { error: string };
        state.error = payload.error;
        state.loading = false;
        toast.error(
          payload.error ||
            "Não foi possível cadastrar o pacote, tente novamente!",
        );
      })

      // Update
      .addCase(updatePackage.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updatePackage.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading = false;
        const index = state.packages.data.findIndex(
          (s) => s.id == action.payload.data.id,
        );
        if (index !== -1) {
          state.packages.data[index] = action.payload.data;
        }
        toast.success("Pacote atualizado com sucesso!");
      })
      .addCase(updatePackage.rejected, (state, action) => {
        const payload = action.payload as { error: string };
        state.error = payload.error;
        state.loading = false;
        toast.error(
          payload.error ||
            "Não foi possível atualizar o pacote, tente novamente!",
        );
      })

      // Delete
      .addCase(deletePackage.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deletePackage.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.packages.data = state.packages.data.filter(
          (s) => String(s.id) != action.payload.data.id,
        );
        toast.success("Pacote excluído com sucesso!");
      })
      .addCase(deletePackage.rejected, (state, action) => {
        state.error = (action.payload as { error: string }).error;
        state.loading = false;
      })

      //packageSaleList
      .addCase(fetchPaymentPackage.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchPaymentPackage.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          const payload = action.payload;

          if (Array.isArray(payload.data)) {
            // Dados não paginados
            state.paymentPackage = {
              data: payload.data,
            };
          } else {
            // Dados paginados
            state.paymentPackage = {
              data: payload.data.data,
              total: payload.data.total,
              currentPage: payload.data.current_page,
              perPage: payload.data.per_page,
            };
          }
        },
      )
      .addCase(fetchPaymentPackage.rejected, (state, action) => {
        state.error = (action.payload as { error: string }).error;
        state.loading = false;
      })

      // StorePackageSale
      .addCase(createPaymentPackage.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        createPaymentPackage.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          state.paymentPackage.data.push(action.payload.data);
          toast.success("Venda de pacote cadastrada com sucesso!");
        },
      )
      .addCase(createPaymentPackage.rejected, (state, action) => {
        const payload = (action.payload as { message: string }).message;
        state.error = payload;
        state.loading = false;

        toast.error(
          (action.payload as { message: string }).message ||
            "Não foi possível cadastrar a venda do pacote, tente novamente!",
        );
      })

      // Update
      .addCase(updatePaymentPackage.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        updatePaymentPackage.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          const index = state.paymentPackage.data.findIndex(
            (s) => s.id == action.payload.data.id,
          );
          if (index !== -1) {
            state.paymentPackage.data[index] = action.payload.data;
          }
          toast.success("Venda de pacote atualizada com sucesso!");
        },
      )
      .addCase(updatePaymentPackage.rejected, (state, action) => {
        const payload = (action.payload as { message: string }).message;
        state.error = action.payload as string;
        state.loading = false;
        toast.error(
          (action.payload as { message: string }).message ||
            "Não foi possível atualizar a venda do pacote, tente novamente",
        );
      })

      // DeletePackageSale
      .addCase(deletePaymentPackage.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        deletePaymentPackage.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          state.paymentPackage.data = state.paymentPackage.data.filter(
            (s) => String(s.id) != action.payload.data.id,
          );
          toast.success("Venda de pacote excluída com sucesso!");
        },
      )
      .addCase(deletePaymentPackage.rejected, (state, action) => {
        const payload = (action.payload as { message: string }).message;
        state.error = action.payload as string;
        state.loading = false;
        toast.error(
          (action.payload as { message: string }).message ||
            "Não foi possível excluir o pacote, tente novamente",
        );
      });
  },
});

export default PackageSlice.reducer;
